/**
 * Cliente HTTP da API de cerimônia (contrato: docs/product/SPEC-API-CERIMONIA.md §3,
 * shapes reais em packages/shared/src/dtos.ts).
 *
 * Auth: token de sessão do signatário (SST — contrato 1 do ledger da Rodada 3B,
 * `docs/reports/LEDGER-RODADA3B-20260711.md`). O token chega na URL do link de assinatura
 * (`?t=...`, ver App.tsx) e vive só em memória neste módulo (nunca localStorage/
 * sessionStorage) — `setSessionToken` é chamado uma vez no bootstrap. A API key de
 * tenant que este módulo usava em dev foi REMOVIDA do bundle (pendência fechada
 * nesta rodada); sem token, toda chamada falha cedo com um erro claro em vez de
 * mandar `Authorization: Bearer null`/indefinido para a API.
 */
import type {
  ApiErrorBody,
  CompleteSessionResponse,
  EnvelopeView,
  OpenSessionRequest,
  SessionView,
  SignerCertificateExpectationView,
  VisualPlacement,
} from '@argink/shared';
import { newTraceId } from './trace';

/**
 * Extensão local de `ApiErrorBody` — `trace_id`/`err_class` são convenção desta
 * rodada (os serviços passam a ecoá-los no corpo de erro); ainda não fazem parte
 * do contrato declarado em `@argink/shared` (fora do meu escopo tocar `packages/shared`
 * nesta rodada — só `apps/signer-ui/**`). Extends em vez de duplicar: se/quando o
 * campo migrar para `@argink/shared`, este tipo continua compatível sem mudança.
 */
export interface ApiErrorBodyWire extends ApiErrorBody {
  trace_id?: string;
  err_class?: string;
  /** D-034 — 422 `certificate_expired`: instante (ISO-8601 UTC) em que o
   * certificado deixou de valer. */
  not_after?: string;
  /** D-034 — 422 `certificate_not_yet_valid`: instante (ISO-8601 UTC) em que o
   * certificado passa a valer. */
  not_before?: string;
  /** D-034 — 500 `artifact_validation_failed`: status devolvido pela validação
   * pós-assinatura do artefato. Não é exibido literalmente na UI (a mensagem é
   * fixa por contrato) — só seria útil em log técnico futuro. */
  validation_status?: string;
}

/**
 * Corpo (wire shape, pré-validação) de POST /v1/signers/:id/session/complete.
 * NÃO reaproveitamos o `CompleteSessionRequest` de @argink/shared aqui de propósito:
 * como `algorithm` tem `.default(...)` no schema Zod, `z.infer` nele expõe o
 * tipo de OUTPUT (pós-parse), onde `algorithm` vira obrigatório — mas o que a
 * UI manda por HTTP é o INPUT (pré-parse), onde é opcional (nível "simples"
 * manda `{}`). Este tipo local descreve o que de fato sai no fetch.
 */
export interface CompleteSessionBody {
  signed_value?: string;
  algorithm?: string;
}

export interface OtpRequestResponse {
  expires_at: string;
}

export interface OtpVerifyResponse {
  verified_at: string;
  channel: 'email';
}

export interface ConsentResponse {
  id: string;
  consent_version: string;
  document_hash: string;
  presented_at: string;
  accepted_at: string;
  session_id: string | null;
}

// Proxy do Vite (vite.config.ts) → apps/api em http://localhost:3300. Evita
// depender de CORS na API (que é headless e não serve browser diretamente).
const API_BASE = '/api';

// Timeout de toda chamada HTTP à API (contrato 5 do ledger da Rodada 3B — item 9).
const API_TIMEOUT_MS = 10_000;

let sessionToken: string | null = null;

/**
 * Define o SST — chamado uma única vez no bootstrap (App.tsx), a partir do `?t=...`
 * da URL do link de assinatura. Fica só em memória (nunca persistido).
 */
export function setSessionToken(token: string): void {
  sessionToken = token;
}

export class ApiError extends Error {
  /** 0 = a requisição nem chegou a sair (rede/DNS/timeout/token ausente). */
  readonly status: number;
  readonly body: ApiErrorBodyWire | null;
  /** trace_id da chamada que originou o erro — sempre presente (gerado localmente
   * quando o servidor não ecoa um em `body.trace_id`). Ver `describeError`. */
  readonly traceId: string;

  constructor(status: number, body: ApiErrorBodyWire | null, traceId: string) {
    super(body?.message ?? `erro da API (HTTP ${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.traceId = traceId;
  }
}

function isTimeout(err: unknown): boolean {
  return err instanceof DOMException && (err.name === 'TimeoutError' || err.name === 'AbortError');
}

/** Lança cedo e com mensagem clara se o SST nunca foi definido (defesa em profundidade —
 * a tela de link inválido em App.tsx já cobre o caminho normal). */
function authHeader(traceId: string): string {
  if (!sessionToken) {
    throw new ApiError(
      0,
      {
        error: 'missing_session_token',
        message: 'token de sessão ausente — abra novamente o link de assinatura original',
        trace_id: traceId,
      },
      traceId,
    );
  }
  return `Bearer ${sessionToken}`;
}

async function request<T>(path: string, init: RequestInit = {}, traceId: string = newTraceId()): Promise<T> {
  const authorization = authHeader(traceId);
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...init,
      signal: AbortSignal.timeout(API_TIMEOUT_MS),
      headers: {
        ...(init.body ? { 'content-type': 'application/json' } : {}),
        authorization,
        'x-argink-trace-id': traceId,
        ...init.headers,
      },
    });
  } catch (err) {
    if (isTimeout(err)) {
      throw new ApiError(
        0,
        { error: 'timeout', message: `a API não respondeu em ${API_TIMEOUT_MS / 1000}s — tente novamente`, trace_id: traceId },
        traceId,
      );
    }
    throw new ApiError(
      0,
      { error: 'network_error', message: 'não foi possível falar com a API — ela pode estar fora do ar', trace_id: traceId },
      traceId,
    );
  }

  const body = (await res.json().catch(() => null)) as T | ApiErrorBodyWire | null;
  if (!res.ok) {
    if (body === null) {
      // Resposta não é JSON: normalmente é o próprio proxy do Vite respondendo
      // (upstream indisponível), não o Fastify — nosso errorHandler central
      // sempre devolve JSON. Mensagem específica em vez do genérico "HTTP 500".
      throw new ApiError(
        res.status,
        {
          error: 'api_unreachable',
          message: `a API não respondeu como esperado (HTTP ${res.status}, sem corpo JSON) — provavelmente está fora do ar`,
          trace_id: traceId,
        },
        traceId,
      );
    }
    throw new ApiError(res.status, body as ApiErrorBodyWire, traceId);
  }
  return body as T;
}

/**
 * GET /v1/envelopes/:id — status completo do envelope e signatários.
 * `traceId`: 1 por operação de negócio ("carregar envelope") — omitido gera um novo
 * a cada chamada (bootstrap e cada "Tentar de novo" são operações distintas).
 */
export function getEnvelope(envelopeId: string, traceId: string = newTraceId()): Promise<EnvelopeView> {
  return request<EnvelopeView>(`/v1/envelopes/${envelopeId}`, {}, traceId);
}

/**
 * POST /v1/signers/:id/session — abre a sessão (cadeia de certs p/ avancada/qualificada).
 * `traceId`: quem chama deve passar o MESMO id usado depois em `authorizeWithAgent`
 * (a dupla forma a operação "autorizar") — ver SignPage.tsx.
 */
export function openSession(
  signerId: string,
  body: OpenSessionRequest,
  traceId: string = newTraceId(),
): Promise<SessionView> {
  return request<SessionView>(
    `/v1/signers/${signerId}/session`,
    { method: 'POST', body: JSON.stringify(body) },
    traceId,
  );
}

export function requestOtp(
  signerId: string,
  traceId: string = newTraceId(),
): Promise<OtpRequestResponse> {
  return request<OtpRequestResponse>(
    `/v1/signers/${signerId}/otp/request`,
    { method: 'POST', body: JSON.stringify({}) },
    traceId,
  );
}

export function verifyOtp(
  signerId: string,
  code: string,
  traceId: string = newTraceId(),
): Promise<OtpVerifyResponse> {
  return request<OtpVerifyResponse>(
    `/v1/signers/${signerId}/otp/verify`,
    { method: 'POST', body: JSON.stringify({ code }) },
    traceId,
  );
}

export function giveConsent(
  signerId: string,
  params: { consentVersion: string; presentedAt: string },
  traceId: string = newTraceId(),
): Promise<ConsentResponse> {
  return request<ConsentResponse>(
    `/v1/signers/${signerId}/consent`,
    {
      method: 'POST',
      body: JSON.stringify({
        consent_version: params.consentVersion,
        presented_at: params.presentedAt,
      }),
    },
    traceId,
  );
}

/**
 * PUT /v1/signers/:id/visual — o signatário grava onde a própria representação visual
 * aparece no documento (M5a).
 *
 * O corpo é o posicionamento em FRAÇÕES da página (origem topo-esquerda, D-027) — nunca pixel
 * de viewport: é isso que faz zoom, scroll e tamanho de tela não moverem o carimbo. A validação
 * (faixa de tamanho e bordas) é do servidor; o clamp da UI é conveniência de interação.
 *
 * `traceId`: 1 por gesto concluído ("reposicionar carimbo") — um arrasto inteiro é uma operação
 * de negócio só, não uma por quadro de movimento.
 *
 * `expectedVersion` vira `If-Match` e é o que protege contra REPLAY: um retry de rede da gravação
 * A que chegue depois da gravação B sobrescreveria B e responderia 200, fazendo a escolha nova
 * sumir sem erro nenhum. Com a precondição, o servidor recusa o obsoleto com 409.
 */
export function setVisualPlacement(
  signerId: string,
  placement: VisualPlacement,
  expectedVersion: number | null = null,
  traceId: string = newTraceId(),
): Promise<{ visual: VisualPlacement; version: number }> {
  return request<{ visual: VisualPlacement; version: number }>(
    `/v1/signers/${signerId}/visual`,
    {
      method: 'PUT',
      body: JSON.stringify(placement),
      ...(expectedVersion !== null ? { headers: { 'if-match': String(expectedVersion) } } : {}),
    },
    traceId,
  );
}

/**
 * POST /v1/signers/:id/session/complete — entrega a assinatura, fecha a cerimônia.
 * `traceId`: quem chama deve passar o MESMO id usado em `signWithAgent` (a dupla
 * forma a operação "assinar") — ver SignPage.tsx.
 */
export function completeSession(
  signerId: string,
  body: CompleteSessionBody,
  traceId: string = newTraceId(),
): Promise<CompleteSessionResponse> {
  return request<CompleteSessionResponse>(
    `/v1/signers/${signerId}/session/complete`,
    { method: 'POST', body: JSON.stringify(body) },
    traceId,
  );
}

/**
 * Monta o caminho de download de um documento já assinado (mesmo formato de
 * `signed_document.download_path` devolvido pelo complete — SPEC §3, ver
 * apps/api/src/routes/signers.ts). Usado quando o signatário reabre o link
 * depois de já ter assinado (GET /v1/envelopes/:id não repete esse campo).
 */
export function signedDownloadPath(envelopeId: string, documentId: string): string {
  return `/v1/envelopes/${envelopeId}/documents/${documentId}/signed`;
}

/**
 * GET /v1/signers/:id/certificate-expectation (D-063 item 2, #92) — token de comparação
 * de mão única para o filtro de certificados por CPF. `cpf_match`/`masked_cpf` nulos =
 * sem expectativa a comparar (sem filtro). Ver `lib/cpf.ts` para o espelho do hash.
 */
export function getCertificateExpectation(
  signerId: string,
  traceId: string = newTraceId(),
): Promise<SignerCertificateExpectationView> {
  return request<SignerCertificateExpectationView>(
    `/v1/signers/${signerId}/certificate-expectation`,
    {},
    traceId,
  );
}

/**
 * GET /v1/envelopes/:id/documents/:docId/content (D-063 item 1, #91) — bytes da revisão
 * CORRENTE do documento (original ou parcialmente assinado), exatamente o que o signatário
 * está prestes a co-assinar. Fora do `request<T>` porque a resposta é binária (PDF), não
 * JSON. `traceId`: 1 por operação "carregar documento" (mount e cada "Tentar de novo").
 */
export async function getDocumentContent(
  envelopeId: string,
  documentId: string,
  traceId: string = newTraceId(),
): Promise<ArrayBuffer> {
  const authorization = authHeader(traceId);
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/v1/envelopes/${envelopeId}/documents/${documentId}/content`, {
      headers: { authorization, 'x-argink-trace-id': traceId },
      signal: AbortSignal.timeout(API_TIMEOUT_MS),
    });
  } catch (err) {
    if (isTimeout(err)) {
      throw new ApiError(
        0,
        { error: 'timeout', message: `o documento não chegou em ${API_TIMEOUT_MS / 1000}s — tente novamente`, trace_id: traceId },
        traceId,
      );
    }
    throw new ApiError(
      0,
      { error: 'network_error', message: 'não foi possível baixar o documento — a API pode estar fora do ar', trace_id: traceId },
      traceId,
    );
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as ApiErrorBodyWire | null;
    throw new ApiError(res.status, body, traceId);
  }
  return res.arrayBuffer();
}

/**
 * POST /v1/envelopes/:id/validate-signed (D-063/#100, lado do signatário) — valida o
 * artefato assinado ARMAZENADO do envelope da cerimônia (motor interno; rota irmã da
 * ação do owner, escopo `eid` da allowlist SST). Só envelope `completed`.
 * Tri-state honesto: `indeterminate` = "não foi possível confirmar toda a cadeia",
 * nunca apresentado como inválido.
 */
export interface SignedValidationView {
  result: 'valid' | 'invalid' | 'indeterminate';
  indication: string;
  sub_indication: string | null;
  signature_count: number;
  checked_at: string;
}

export function validateSignedEnvelope(
  envelopeId: string,
  traceId: string = newTraceId(),
): Promise<SignedValidationView> {
  return request<SignedValidationView>(
    `/v1/envelopes/${envelopeId}/validate-signed`,
    { method: 'POST', body: JSON.stringify({}) },
    traceId,
  );
}

/** GET .../signed (autenticado) — baixa os bytes do artefato assinado. `traceId`: 1 por clique em "Baixar". */
export async function downloadSigned(downloadPath: string, traceId: string = newTraceId()): Promise<Blob> {
  const authorization = authHeader(traceId);
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${downloadPath}`, {
      headers: { authorization, 'x-argink-trace-id': traceId },
      signal: AbortSignal.timeout(API_TIMEOUT_MS),
    });
  } catch (err) {
    if (isTimeout(err)) {
      throw new ApiError(
        0,
        { error: 'timeout', message: `o download não respondeu em ${API_TIMEOUT_MS / 1000}s — tente novamente`, trace_id: traceId },
        traceId,
      );
    }
    throw new ApiError(
      0,
      { error: 'network_error', message: 'não foi possível baixar o documento — a API pode estar fora do ar', trace_id: traceId },
      traceId,
    );
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as ApiErrorBodyWire | null;
    throw new ApiError(res.status, body, traceId);
  }
  return res.blob();
}
